/**
 * Dashboard SPA entry — RouteDef factory per tab + the umbrella
 * `dashboardRoutes` export consumed by `main.ts`.
 *
 * `buildDashboardRoute(tab)` is the central piece: it returns a RouteDef
 * whose `render()` returns shellHtml(tab, PANELS[tab]()) and whose
 * `mount()` wires up the per-tab interactivity (eSIM grid auto-refresh,
 * analytics chart, history filters, settings forms, etc.). The mount
 * function dispatches by tab and only attaches handlers relevant to
 * that tab — large but flat, no cross-tab plumbing.
 *
 * Originally lines 1283-1291 + 1425-end of `routes/dashboard.ts`.
 * Phase 2.2 split 2026-05-12.
 */
import type { RouteDef, RouteContext } from '../../router';
import { googleButtonWidth } from '../../modals/auth';
import { icon } from '../../utils/icons';
import { fmtDate } from '../../utils/format';
import { Auth } from '../../auth';
import { escapeHtml } from '../../utils';
import * as api from '../../api';
import { showToast } from '../../toast';
import { t, getLocale, withLocale } from '../../i18n';
import { mainButton as tgMainButton, haptic as tgHaptic, tgIsAvailable } from '../../telegram-mini-app';
import { PWA_BANNER_DISMISS_KEY, TAB_ORDER, type Tab, isAppStandalone, shellHtml, tabLabel } from './_shared';
import {
  esimsPanel,
  loadEsimsInto,
  startCountdownTicker,
  handleEsimAction,
  handleTopUpReturnQuery,
  resumePendingEsim,
} from './esims';
import { analyticsPanel, loadAnalyticsInto, loadBreakdown } from './analytics';
import {
  historyPanel,
  loadHistoryInto,
  wireHistoryFilters,
} from './history';
import { supportPanel } from './support';
import { settingsPanel } from './settings';
import { referralPanel, wireReferralHandlers } from './referral';

const PANELS: Record<Tab, () => string> = {
  esims: esimsPanel,
  analytics: analyticsPanel,
  history: historyPanel,
  referral: referralPanel,
  support: supportPanel,
  settings: settingsPanel,
};

/* ─── Per-tab mount (interactivity + data fetch) ──────────── */

function buildDashboardRoute(tab: Tab): RouteDef {
  return {
    path: `/dashboard/${tab}`,
    routeType: '404',
    authRequired: true,
    metaTitle: `${tabLabel(tab)} — Roamzy Dashboard`,
    metaDesc: 'Your eSIMs and account.',
    render(_ctx: RouteContext) {
      return shellHtml(tab, PANELS[tab]());
    },
    mount(_root) {
      const cleanups: Array<() => void> = [];

      // On a phone the tab strip scrolls sideways, and on Support or Settings
      // the active tab sat off the right edge — the page gave no sign of
      // where you were (2026-09-29). Scroll the strip itself, never the page.
      const strip = _root.querySelector<HTMLElement>('.dash-tabs');
      const activeTab = strip?.querySelector<HTMLElement>('.dash-tab.is-active');
      if (strip && activeTab && strip.scrollWidth > strip.clientWidth) {
        strip.scrollLeft = activeTab.offsetLeft - (strip.clientWidth - activeTab.offsetWidth) / 2;
      }

      // Bump engagement counter on every dashboard mount (debounced
      // once per browser session so reloads don't double-count). The
      // banner's render-time gate uses this number to skip the first
      // few visits — operator feedback 2026-05-09: smart-prompt only
      // after the user has shown they keep coming back.
      void import('../../pwa-install').then(({ bumpDashboardVisit }) => bumpDashboardVisit());

      // Referral attribution (mig 0048): bind any captured ?ref code now that
      // the user is authenticated. Idempotent + first-touch on the server.
      void import('../../referral-attr').then(({ flushRefAttribution }) => flushRefAttribution());

      // PWA + push banner — present in DOM only when `shouldShowPwaBanner()`
      // returned true at render time. Three controls:
      //   ✕                     → permanent dismiss (localStorage flag)
      //   "Add to home screen"  → native install (Chrome/Edge) OR /guides#pwa
      //   "Enable alerts"       → in-place pushManager.subscribe() flow
      // The "Enable alerts" button is hidden in HTML; we reveal it after
      // checking that push is supported AND the user isn't already
      // subscribed. Otherwise it would tease a non-functional CTA on
      // browsers without push support (e.g. Safari < 16.4).
      const banner = document.querySelector<HTMLElement>('[data-pwa-banner]');
      if (banner) {
        const closeBtn = banner.querySelector<HTMLButtonElement>('[data-pwa-banner-close]');
        const enableBtn = banner.querySelector<HTMLButtonElement>('[data-pwa-banner-enable]');
        const pinLink = banner.querySelector<HTMLAnchorElement>('[data-pwa-banner-pin]');

        const onDismiss = (): void => {
          banner.remove();
          try { localStorage.setItem(PWA_BANNER_DISMISS_KEY, '1'); } catch { /* private mode etc */ }
        };
        closeBtn?.addEventListener('click', onDismiss);
        cleanups.push(() => closeBtn?.removeEventListener('click', onDismiss));

        // Reveal the "Enable alerts" CTA only when push is supported AND
        // the user isn't subscribed yet. If they're already subscribed we
        // hide the alerts CTA and keep only the pin nudge.
        void (async () => {
          try {
            const { getPushSupport, getCurrentSubscription, subscribePush } = await import('../../push');
            const support = await getPushSupport();
            if (!support.supported || support.permission === 'granted') {
              // Push unsupported, OR already granted — check actual subscription.
              const sub = await getCurrentSubscription();
              if (sub) {
                // Already subscribed → hide the alerts CTA, banner becomes pin-only.
                if (enableBtn) enableBtn.hidden = true;
                return;
              }
              if (!support.supported) {
                if (enableBtn) enableBtn.hidden = true;
                return;
              }
            }
            // Push is supported AND not yet subscribed — reveal the CTA.
            if (enableBtn) {
              enableBtn.hidden = false;
              const onEnable = async (): Promise<void> => {
                enableBtn.disabled = true;
                const orig = enableBtn.textContent;
                enableBtn.textContent = '…';
                try {
                  await subscribePush();
                  showToast(t('toast.pushEnabled'), 'success');
                  // Replace the button with a quiet confirmation, leave
                  // the pin CTA. If user has also installed PWA, the next
                  // mount will hide the whole banner via shouldShowPwaBanner.
                  enableBtn.textContent = '✓';
                  enableBtn.disabled = true;
                } catch (err) {
                  const msg = (err as Error).message;
                  if (msg === 'permission-denied') {
                    showToast(t('toast.pushPermissionDenied'), 'error');
                  } else {
                    showToast(`${t('toast.pushFailed')}: ${msg}`, 'error');
                  }
                  enableBtn.textContent = orig;
                  enableBtn.disabled = false;
                }
              };
              enableBtn.addEventListener('click', onEnable);
              cleanups.push(() => enableBtn.removeEventListener('click', onEnable));
            }
          } catch { /* push module load failed — leave banner as pin-only */ }
        })();

        // If the app is already running standalone (PWA installed) we
        // don't need the pin nudge — hide that link too. Banner then
        // shows only the alerts CTA.
        if (pinLink && isAppStandalone()) {
          pinLink.hidden = true;
        }

        // Native install on click — if the browser fired a captured
        // `beforeinstallprompt`, intercept the link and trigger the
        // native install dialog instead of routing to /guides#pwa.
        // Falls back to the link nav when no event is available
        // (iOS Safari, Firefox), so the CTA always does something
        // useful. Operator request 2026-05-09: smart prompt.
        if (pinLink) {
          const onPinClick = async (e: Event): Promise<void> => {
            const { isNativeInstallReady, triggerNativeInstall } = await import('../../pwa-install');
            if (!isNativeInstallReady()) return; // let the <a> nav happen
            e.preventDefault();
            const outcome = await triggerNativeInstall();
            if (outcome === 'accepted') {
              // `appinstalled` fires next, the global handler will
              // also auto-subscribe push. We don't need to do anything
              // here beyond closing visual state.
              showToast(t('toast.pwaInstalling'), 'success');
            }
          };
          pinLink.addEventListener('click', onPinClick);
          cleanups.push(() => pinLink.removeEventListener('click', onPinClick));
        }
      }

      if (tab === 'esims') {
        const mount = document.querySelector<HTMLElement>('[data-esims-mount]');
        if (mount) void loadEsimsInto(mount);
        void resumePendingEsim();
        handleTopUpReturnQuery();

        // Telegram Mini App: sticky MainButton at the bottom of the
        // Mini App viewport — primary CTA "Add eSIM" → triggers the
        // existing `data-action="add-new-esim"` handler (always opens
        // CREATE, never falls through to topup-existing — same as the
        // "+ Add eSIM" tile on the grid). Pushed once per dashboard
        // mount; popped on unmount via the cleanup chain. No-op on web.
        if (tgIsAvailable()) {
          const tgUnmount = tgMainButton.push({
            text: t('dash.esims.addEsim'),
            onClick: async () => {
              tgHaptic.tap();
              // Synthesize the same event the in-page tile fires: a real
              // click on a button with [data-action="add-new-esim"]. If
              // the tile exists in DOM we click it; if the user is on the
              // empty-state view (no grid yet) we dispatch a synthetic
              // click on the body — main.ts's `click` delegation matches
              // by [data-action] closest, but here we want to drive the
              // same `add-new-esim` branch. Cleanest: directly call the
              // exported flow via a custom event the handler also listens
              // for — but we don't have such an event today. Easiest:
              // create a stub <button> with the right dataset, click it,
              // detach. Single source of truth (main.ts handler), zero
              // duplication.
              const tile = document.querySelector<HTMLButtonElement>(
                '[data-action="add-new-esim"]',
              );
              if (tile) {
                tile.click();
              } else {
                // Empty-state: the explicit "+ Add eSIM" tile isn't on
                // the page (the empty-state CTA uses `open-topup`). For
                // empty users, open-topup auto-routes to CREATE (see
                // main.ts:133), so click the empty CTA instead.
                const emptyCta = document.querySelector<HTMLButtonElement>(
                  '.dash-empty [data-action="open-topup"]',
                );
                emptyCta?.click();
              }
              await Promise.resolve();
            },
          });
          cleanups.push(tgUnmount);
        }

        // Auto-refresh the eSIM grid every 60s while the tab is visible
        // (operator request 2026-05-08: status pill was stale because the
        // card never refetched after first render). Catches any state
        // change driven server-side: venta-sync cron updating installedAt
        // / lastSeenAt, admin archive actions, IPN-triggered activations
        // arriving while the user watches. Pause when the tab is hidden
        // to avoid burning bandwidth on a backgrounded dashboard.
        const REFRESH_MS = 60_000;
        let refreshTimer: number | null = null;
        const refreshIfVisible = (): void => {
          if (document.visibilityState !== 'visible') return;
          const m = document.querySelector<HTMLElement>('[data-esims-mount]');
          if (m) void loadEsimsInto(m);
        };
        const startTimer = (): void => {
          if (refreshTimer !== null) return;
          refreshTimer = window.setInterval(refreshIfVisible, REFRESH_MS);
        };
        const stopTimer = (): void => {
          if (refreshTimer === null) return;
          window.clearInterval(refreshTimer);
          refreshTimer = null;
        };
        const onVisibility = (): void => {
          if (document.visibilityState === 'visible') {
            // Refresh immediately on tab refocus — operator switched away,
            // possibly removed the eSIM from phone, came back: should see
            // current state without waiting for the 60s tick.
            refreshIfVisible();
            startTimer();
          } else {
            stopTimer();
          }
        };
        startTimer();
        document.addEventListener('visibilitychange', onVisibility);
        cleanups.push(() => { stopTimer(); document.removeEventListener('visibilitychange', onVisibility); });

        // When the device transitions from offline → online, immediately
        // refresh the eSIM grid so the stale-snapshot badge gets replaced
        // with live data without making the user wait for the next 60s
        // tick. Operator-facing benefit: PWA on a phone in airplane mode
        // → unlock airplane mode → list refreshes within a second.
        const onOnline = (): void => { refreshIfVisible(); };
        window.addEventListener('online', onOnline);
        cleanups.push(() => window.removeEventListener('online', onOnline));

        const onClick = (e: Event): void => {
          const btn = (e.target as Element | null)?.closest<HTMLElement>('[data-esim-action]');
          if (!btn) return;
          const action = btn.dataset.esimAction;
          const id = btn.dataset.esimId;
          if (action && id) void handleEsimAction(action, id, document.body, btn);
        };
        document.addEventListener('click', onClick);
        cleanups.push(() => document.removeEventListener('click', onClick));

        // Free-trial eSIM (mig 0054). One click issues the eSIM with the trial
        // credit and opens its QR — the person sees the service work before
        // paying anything. Errors stay inside the card; the button re-enables.
        const onTrialClick = (e: Event): void => {
          const btn = (e.target as Element | null)?.closest<HTMLButtonElement>('[data-action="claim-trial"]');
          if (!btn) return;
          e.preventDefault();
          const card = btn.closest<HTMLElement>('[data-trial-card]');
          const status = card?.querySelector<HTMLElement>('[data-trial-status]');
          const say = (text: string, error = false): void => {
            if (!status) return;
            status.textContent = text;
            status.hidden = false;
            status.classList.toggle('dash-trial__status--error', error);
          };
          btn.disabled = true;
          say(t('dash.trial.claiming'));
          void (async () => {
            const { claimTrial, trialErrorText } = await import('./esims');
            const r = await claimTrial();
            if (!r.ok) {
              const why = trialErrorText(r.error);
              btn.disabled = why.settled;
              say(why.text, true);
              if (why.settled) {
                const listMount = document.querySelector<HTMLElement>('[data-esims-mount]');
                if (listMount) setTimeout(() => { void loadEsimsInto(listMount); }, 2500);
              }
              return;
            }
            showToast(t('dash.trial.done'), 'success');
            const mount = document.querySelector<HTMLElement>('[data-esims-mount]');
            if (mount) await loadEsimsInto(mount);
            try {
              const resp = await fetch(`/api/esims/${r.esimId}`, { credentials: 'include' });
              if (resp.ok) {
                const data = (await resp.json()) as { esim: { qrPayload?: string | null; lpaUri?: string | null } };
                const { openQrModal } = await import('../../modals/qr');
                openQrModal({
                  countryName: t('dash.esims.global'),
                  flag: '🌐',
                  qrPayload: data.esim.qrPayload ?? '',
                  lpaUri: data.esim.lpaUri ?? '',
                });
              }
            } catch { /* the card on the grid still offers "Show QR" */ }
          })();
        };
        document.addEventListener('click', onTrialClick);
        cleanups.push(() => document.removeEventListener('click', onTrialClick));

        // Click-to-copy on the displayId pill.
        const onIdClick = (e: Event): void => {
          const target = (e.target as Element | null)?.closest<HTMLElement>('[data-esim-display-id]');
          if (!target) return;
          const id = target.dataset.esimDisplayId;
          if (!id) return;
          void navigator.clipboard?.writeText(id);
          showToast(t('toast.idCopied'), 'success');
        };
        document.addEventListener('click', onIdClick);
        cleanups.push(() => document.removeEventListener('click', onIdClick));

        // Status pill click → open detail modal (RSP profile + activity).
        const onStatusClick = (e: Event): void => {
          const btn = (e.target as Element | null)?.closest<HTMLElement>('[data-esim-status]');
          if (!btn) return;
          e.stopPropagation();
          const esimId = btn.dataset.esimStatus;
          if (!esimId) return;
          void (async () => {
            const { openEsimStatusModal } = await import('../../modals/esim-status');
            void openEsimStatusModal(esimId);
          })();
        };
        document.addEventListener('click', onStatusClick);
        cleanups.push(() => document.removeEventListener('click', onStatusClick));

        // Inline label edit (mig 0025): pencil button opens the rename
        // modal. Until 2026-05-08 this used the native `prompt()` which
        // ignored our typography and on iOS got occluded by the keyboard;
        // we now render our own modal with a separate Remove action so
        // clearing is discoverable. PATCH still happens in the modal.
        const onLabelEdit = (e: Event): void => {
          const btn = (e.target as Element | null)?.closest<HTMLElement>('[data-esim-label-edit]');
          if (!btn) return;
          e.stopPropagation();
          const esimId = btn.dataset.esimLabelEdit;
          if (!esimId) return;
          // Read currentLabel from the heading. The pencil button itself
          // sits inside the heading's text node, so trimming the first
          // text node gets us the displayed label (or the i18n fallback
          // "Глобальный eSIM" when no label is set).
          const heading = document.querySelector<HTMLElement>(`[data-esim-label-display="${esimId}"]`);
          const displayed = heading?.firstChild?.textContent?.trim() ?? '';
          // The fallback string (t('dash.esims.global')) means "no label" —
          // pass null so the modal pre-fills empty rather than the literal
          // word "Глобальный eSIM" / "Global eSIM" / etc.
          const fallback = t('dash.esims.global');
          const currentLabel = displayed && displayed !== fallback ? displayed : null;
          void (async () => {
            const { openRenameModal } = await import('../../modals/rename');
            openRenameModal({
              esimId,
              currentLabel,
              onSaved: async () => {
                const mount = document.querySelector<HTMLElement>('[data-esims-mount]');
                if (mount) await loadEsimsInto(mount);
              },
            });
          })();
        };
        document.addEventListener('click', onLabelEdit);
        cleanups.push(() => document.removeEventListener('click', onLabelEdit));

        // Reservation countdown ticker.
        cleanups.push(startCountdownTicker(document));

        // Country breakdown: load when <details> opens; switch on tab click.
        const breakdownOpen = (e: Event): void => {
          const det = e.target as HTMLDetailsElement;
          if (!det.matches?.('details[data-esim-breakdown]')) return;
          if (!det.open) return;
          const esimId = det.dataset.esimBreakdown;
          const body = det.querySelector<HTMLElement>('[data-esim-breakdown-body]');
          if (esimId && body && !body.dataset.loaded) {
            body.dataset.loaded = '1';
            void loadBreakdown(esimId, 7, body);
          }
        };
        document.addEventListener('toggle', breakdownOpen, true);
        cleanups.push(() => document.removeEventListener('toggle', breakdownOpen, true));

        const breakdownTabClick = (e: Event): void => {
          const tab = (e.target as Element | null)?.closest<HTMLElement>('.dash-esim-card__breakdown-tab');
          if (!tab) return;
          const det = tab.closest<HTMLElement>('[data-esim-breakdown]');
          const esimId = det?.dataset.esimBreakdown;
          const body = det?.querySelector<HTMLElement>('[data-esim-breakdown-body]');
          const days = Number(tab.dataset.days);
          if (!esimId || !body || ![7, 14, 30, 90].includes(days)) return;
          det?.querySelectorAll('.dash-esim-card__breakdown-tab').forEach((t) => t.classList.remove('is-active'));
          tab.classList.add('is-active');
          void loadBreakdown(esimId, days as 7 | 14 | 30 | 90, body);
        };
        document.addEventListener('click', breakdownTabClick);
        cleanups.push(() => document.removeEventListener('click', breakdownTabClick));
      } else if (tab === 'analytics') {
        const mount = document.querySelector<HTMLElement>('[data-analytics-mount]');
        const period = document.querySelector<HTMLElement>('[data-analytics-period]');
        const select = document.querySelector<HTMLSelectElement>('[data-analytics-esim-select]');
        let days: 7 | 30 | 90 = 30;
        let selectedEsimId = '';
        const reload = (): void => {
          if (mount) void loadAnalyticsInto(mount, days, selectedEsimId || undefined);
        };
        reload();
        if (period) {
          const onPeriodClick = (e: Event): void => {
            const btn = (e.target as Element | null)?.closest<HTMLButtonElement>('.dash-period-btn');
            if (!btn) return;
            const d = Number(btn.dataset.days) as 7 | 30 | 90;
            if (![7, 30, 90].includes(d) || d === days) return;
            days = d;
            period.querySelectorAll('.dash-period-btn').forEach((b) => b.classList.toggle('is-active', b === btn));
            reload();
          };
          period.addEventListener('click', onPeriodClick);
          cleanups.push(() => period.removeEventListener('click', onPeriodClick));
        }
        if (select) {
          // Live-listen on the select even though loadAnalyticsInto re-renders
          // its options on every reload — `change` fires before re-render so
          // we capture the new value first, then reload calls /api/me/stats
          // with it. The picker stays in sync because the API echoes
          // selectedEsimId back into r.selectedEsimId → select.value.
          const onChange = (): void => {
            selectedEsimId = select.value;
            reload();
          };
          select.addEventListener('change', onChange);
          cleanups.push(() => select.removeEventListener('change', onChange));
        }
        // Country list expand toggle. Delegated from `mount` (not
        // direct-bound on the button) because loadAnalyticsInto rebuilds
        // its innerHTML on every period/eSIM change, so direct listeners
        // would die on first reload. The toggle button + extra-tbody
        // markup is in `byCountryRows` above.
        if (mount) {
          const onCountryToggle = (e: Event): void => {
            const btn = (e.target as Element | null)?.closest<HTMLButtonElement>('[data-country-toggle]');
            if (!btn) return;
            e.preventDefault();
            const section = btn.closest<HTMLElement>('.dash-analytics__section');
            const extra = section?.querySelector<HTMLElement>('[data-country-extra]');
            const toggleRow = section?.querySelector<HTMLElement>('[data-country-toggle-row]');
            if (!extra || !toggleRow) return;
            const expanded = btn.getAttribute('aria-expanded') === 'true';
            if (expanded) {
              extra.setAttribute('hidden', '');
              btn.setAttribute('aria-expanded', 'false');
              btn.textContent = btn.dataset.labelShow ?? btn.textContent ?? '';
            } else {
              extra.removeAttribute('hidden');
              btn.setAttribute('aria-expanded', 'true');
              // Cache the original label so we can restore it on collapse.
              if (!btn.dataset.labelShow) btn.dataset.labelShow = btn.textContent ?? '';
              btn.textContent = t('dash.analytics.showFewerCountries');
            }
          };
          mount.addEventListener('click', onCountryToggle);
          cleanups.push(() => mount.removeEventListener('click', onCountryToggle));
        }
      } else if (tab === 'history') {
        const rows = document.querySelector<HTMLElement>('[data-history-rows]');
        const emptyEl = document.querySelector<HTMLElement>('[data-history-empty]');
        if (rows) void loadHistoryInto(rows, emptyEl);
        // Filter pills are part of the static panel HTML (not rebuilt by
        // loadHistoryInto), so wire once on tab activation. Cleanup
        // tracked via the tab cleanups[] so we don't leak listeners on
        // tab switch. The closest section element is the scope.
        const historyScope = rows?.closest<HTMLElement>('section, .dash-tab-content') ?? document.body;
        const cleanupFilters = wireHistoryFilters(historyScope);
        if (cleanupFilters) cleanups.push(cleanupFilters);
        void resumePendingEsim();
      } else if (tab === 'support') {
        // Pre-fill MSISDN of the user's most-recent active eSIM into TG/email
        // support links so the operator can identify the conversation without
        // asking "what's your eSIM ID?". Operator decision 2026-05-08.
        //
        // Strategy:
        //   - Fetch eSIMs once on tab mount (independent from /esims tab cache).
        //   - Pick the first 'active' eSIM with a non-null msisdn; fallback to
        //     any eSIM with msisdn; fallback to no prefill (links work as-is).
        //   - Email: mailto with subject + body template carrying the MSISDN.
        //   - Telegram bot: `?start=esim_<msisdn>` payload — bots don't accept
        //     `?text=`, only `?start=PAYLOAD`. The bot can read this payload
        //     server-side via Telegram API to recognise the user immediately;
        //     even if the bot doesn't yet handle it, Telegram surfaces the
        //     payload as the first user message after they tap "Start".
        void (async () => {
          const tgCard = document.querySelector<HTMLAnchorElement>('[data-support-action="tg"]');
          const mailCard = document.querySelector<HTMLAnchorElement>('[data-support-action="email"]');
          if (!tgCard && !mailCard) return;
          let msisdn: string | null = null;
          try {
            const list = await api.fetchEsims();
            const active = list.esims.find((e) => e.status === 'active' && e.msisdn);
            const anyWithMsisdn = list.esims.find((e) => e.msisdn);
            msisdn = active?.msisdn ?? anyWithMsisdn?.msisdn ?? null;
          } catch { /* leave links as-is — no prefill is acceptable */ }
          if (!msisdn) return;
          if (tgCard) {
            tgCard.href = `https://t.me/roamzy_support_bot?start=esim_${encodeURIComponent(msisdn)}`;
          }
          if (mailCard) {
            const subject = encodeURIComponent(`Roamzy support · eSIM ${msisdn}`);
            const body = encodeURIComponent(`eSIM ID: ${msisdn}\n\n`);
            mailCard.href = `mailto:support@roamzy.io?subject=${subject}&body=${body}`;
          }
        })();
      } else if (tab === 'settings') {
        const form = document.querySelector<HTMLFormElement>('[data-email-form]');
        const input = form?.querySelector<HTMLInputElement>('[data-email-input]');
        const btn = form?.querySelector<HTMLButtonElement>('[data-email-save]');
        if (form && input && btn) {
          const onSubmit = async (e: Event): Promise<void> => {
            e.preventDefault();
            const value = input.value.trim();
            // Allow clearing to null only if the field is empty AND user has
            // an email (i.e. they explicitly want to remove it). Most users
            // are filling, not clearing.
            const next = value === '' ? null : value;
            btn.disabled = true;
            const original = btn.textContent;
            btn.textContent = t('dash.settings.emailSaving');
            try {
              await api.patchMeEmail(next);
              // exactOptionalPropertyTypes won't accept `email: undefined` in
              // a Partial<User>; either set the email or pass an empty patch
              // and let the next /api/me on page reload pick up the cleared
              // value. Empty patch is the right call for the "cleared" path.
              if (next) Auth.updateUserLocal({ email: next });
              showToast(t('toast.emailUpdated'), 'success');
            } catch (err) {
              showToast(`${t('toast.emailUpdateFailed')}: ${(err as Error).message}`, 'error');
            } finally {
              btn.disabled = false;
              btn.textContent = original;
            }
          };
          form.addEventListener('submit', onSubmit);
          cleanups.push(() => form.removeEventListener('submit', onSubmit));
        }

        // Push notifications block — feature-detect, then show/hide UI.
        // The whole `data-push-section` is hidden by default; we reveal
        // it only when the browser supports push AND the backend has
        // a VAPID key configured (read via /api/config).
        void (async () => {
          const pushSection = document.querySelector<HTMLElement>('[data-push-section]');
          if (!pushSection) return;
          const { getPushSupport, getCurrentSubscription, subscribePush, unsubscribePush, getPrefs, updatePrefs } = await import('../../push');

          const support = await getPushSupport();
          if (!support.supported) {
            // Hide section entirely on unsupported browsers / unavailable VAPID.
            return;
          }
          pushSection.hidden = false;

          const statusEl = pushSection.querySelector<HTMLElement>('[data-push-status]');
          const toggleBtn = pushSection.querySelector<HTMLButtonElement>('[data-push-toggle]');
          const categoriesEl = pushSection.querySelector<HTMLElement>('[data-push-categories]');
          const prefInputs = pushSection.querySelectorAll<HTMLInputElement>('[data-push-pref]');

          const refresh = async (): Promise<void> => {
            const sub = await getCurrentSubscription();
            const subscribed = !!sub && Notification.permission === 'granted';
            if (toggleBtn) {
              toggleBtn.disabled = false;
              toggleBtn.textContent = subscribed
                ? t('dash.settings.pushBtnDisable')
                : t('dash.settings.pushBtnEnable');
            }
            if (statusEl) {
              statusEl.textContent = subscribed
                ? t('dash.settings.pushStatusActive')
                : Notification.permission === 'denied'
                  ? t('dash.settings.pushStatusBlocked')
                  : t('dash.settings.pushStatusInactive');
            }
            if (categoriesEl) categoriesEl.hidden = !subscribed;
            if (subscribed) {
              try {
                const prefs = await getPrefs();
                prefInputs.forEach((inp) => {
                  const k = inp.dataset.pushPref as 'lowBalance' | 'paymentPaid' | 'esimActivated' | 'countryWelcome' | 'inactivePing';
                  if (k && k in prefs) inp.checked = (prefs as unknown as Record<string, boolean>)[k] ?? false;
                });
              } catch { /* prefs fetch can fail offline; UI stays at defaults */ }
            }
          };

          await refresh();

          const onToggle = async (): Promise<void> => {
            if (!toggleBtn) return;
            toggleBtn.disabled = true;
            try {
              const sub = await getCurrentSubscription();
              if (sub) {
                await unsubscribePush();
                showToast(t('toast.pushDisabled'), 'success');
              } else {
                await subscribePush();
                showToast(t('toast.pushEnabled'), 'success');
              }
            } catch (err) {
              const msg = (err as Error).message;
              if (msg === 'permission-denied') {
                showToast(t('toast.pushPermissionDenied'), 'error');
              } else {
                showToast(`${t('toast.pushFailed')}: ${msg}`, 'error');
              }
            } finally {
              await refresh();
            }
          };
          toggleBtn?.addEventListener('click', onToggle);
          cleanups.push(() => toggleBtn?.removeEventListener('click', onToggle));

          const onPrefChange = async (e: Event): Promise<void> => {
            const inp = e.currentTarget as HTMLInputElement;
            const key = inp.dataset.pushPref as 'lowBalance' | 'paymentPaid' | 'esimActivated' | 'countryWelcome' | 'inactivePing' | undefined;
            if (!key) return;
            try {
              await updatePrefs({ [key]: inp.checked });
            } catch (err) {
              showToast(`${t('toast.pushFailed')}: ${(err as Error).message}`, 'error');
              inp.checked = !inp.checked; // revert
            }
          };
          prefInputs.forEach((inp) => {
            inp.addEventListener('change', onPrefChange);
            cleanups.push(() => inp.removeEventListener('change', onPrefChange));
          });

        })();
      } else if (tab === 'referral') {
        void wireReferralHandlers();
      }

      // ── API tokens section (Phase 3, 2026-05-20) ──────────────────────
      // Renders "API & Agents" panel with CRUD for Bearer tokens. Tokens
      // power AI agents (Claude / Cursor / Continue / MCP) on user's behalf.
      void (async () => {
        const { wireApiTokensHandlers } = await import('./api-tokens');
        wireApiTokensHandlers();
      })();

      // ── Approval channel (mig 0052, 2026-08-18) ───────────────────────
      // Binds the human factor that approves an agent's large purchases.
      // Browser-only by design: the backend routes read the session cookie,
      // so an API token can never create or revoke its own approver.
      void (async () => {
        const { wireConfirmChannelHandlers } = await import('./confirm-channel');
        wireConfirmChannelHandlers();
      })();

      // ── Passkeys section (mig 0040, 2026-05-09) ───────────────────────
      // Hidden by default; revealed when WebAuthn is supported. Lists
      // user's registered passkeys with add/remove. Shows nothing inside
      // Telegram Mini App (passkey API blocked there).
      const passkeySection = document.querySelector<HTMLElement>('[data-passkey-section]');
      if (passkeySection) {
        void (async () => {
          const { isPasskeySupported, listPasskeys, registerPasskey, removePasskey, reconcilePasskeyFlag } = await import('../../passkeys');
          if (!isPasskeySupported()) {
            // Section stays hidden — no point showing UI for unsupported browsers.
            return;
          }
          passkeySection.hidden = false;
          // Reconcile localStorage flag with server truth on settings open.
          // Catches the case where user registered passkey on iPhone (iCloud
          // Keychain syncs the credential to Mac, but localStorage doesn't).
          // Operator caught flag desync 2026-05-09 поздний вечер +15.
          void reconcilePasskeyFlag();

          const listEl = passkeySection.querySelector<HTMLElement>('[data-passkey-list]');
          const addBtn = passkeySection.querySelector<HTMLButtonElement>('[data-passkey-add]');
          const statusEl = passkeySection.querySelector<HTMLElement>('[data-passkey-status]');

          const renderList = (passkeys: Array<{ id: string; deviceName: string | null; createdAt: string; lastUsedAt: string | null }>): void => {
            if (!listEl) return;
            if (passkeys.length === 0) {
              listEl.innerHTML = `<p class="dash-sub" style="margin: 0 0 12px;" data-i18n="dash.settings.passkeysEmpty">${escapeHtml(t('dash.settings.passkeysEmpty'))}</p>`;
              return;
            }
            listEl.innerHTML = passkeys.map((pk) => {
              const created = fmtDate(pk.createdAt);
              const lastUsed = pk.lastUsedAt
                ? fmtDate(pk.lastUsedAt)
                : t('dash.settings.passkeysNeverUsed');
              const name = pk.deviceName ? escapeHtml(pk.deviceName) : '—';
              return `<div class="dash-pref dash-pref--passkey">
                <span>
                  <strong class="dash-passkey__name">${icon('lock', 14)} ${name}</strong>
                  <span class="dash-sub">${escapeHtml(t('dash.settings.passkeysAddedAt'))}: ${escapeHtml(created)} · ${escapeHtml(t('dash.settings.passkeysLastUsed'))}: ${escapeHtml(lastUsed)}</span>
                </span>
                <button class="btn btn--ghost btn--sm" data-passkey-remove="${escapeHtml(pk.id)}" data-i18n="dash.settings.passkeysRemove">${escapeHtml(t('dash.settings.passkeysRemove'))}</button>
              </div>`;
            }).join('');
          };

          const refresh = async (): Promise<void> => {
            const list = await listPasskeys();
            renderList(list);
          };

          await refresh();

          const onAdd = async (): Promise<void> => {
            if (!addBtn || !statusEl) return;
            addBtn.disabled = true;
            statusEl.textContent = t('auth.verifying');
            const r = await registerPasskey();
            addBtn.disabled = false;
            if (r.ok) {
              statusEl.textContent = '';
              showToast(t('auth.passkey.registeredToast'), 'success');
              await refresh();
              return;
            }
            if (r.reason === 'user_cancelled') {
              // Silent — user explicitly dismissed the OS prompt. Clear
              // status so the click feedback resets cleanly.
              statusEl.textContent = '';
              return;
            }
            // Non-cancel failure (verify_failed, options_failed, unknown,
            // not_supported). Surface via toast so the operator/user has
            // immediate signal that the click did something + failed —
            // previously the click looked like a no-op which was confusing.
            statusEl.textContent = t('auth.passkey.failedBody');
            showToast(t('auth.passkey.failedBody'), 'error');
          };
          addBtn?.addEventListener('click', onAdd);
          cleanups.push(() => addBtn?.removeEventListener('click', onAdd));

          const onListClick = async (e: Event): Promise<void> => {
            const target = e.target as Element | null;
            const removeBtn = target?.closest<HTMLElement>('[data-passkey-remove]');
            if (!removeBtn) return;
            const id = removeBtn.dataset.passkeyRemove;
            if (!id) return;
            if (!window.confirm(t('dash.settings.passkeysRemoveConfirm'))) return;
            removeBtn.setAttribute('disabled', '');
            const ok = await removePasskey(id);
            if (ok) {
              showToast(t('toast.saved'), 'success');
              await refresh();
            } else {
              removeBtn.removeAttribute('disabled');
              showToast(t('auth.passkey.failedBody'), 'error');
            }
          };
          listEl?.addEventListener('click', onListClick);
          cleanups.push(() => listEl?.removeEventListener('click', onListClick));
        })();
      }

      // ── Linked accounts section (account merge, поздний вечер +11) ────
      // Lists current providers + lets user link Google or Telegram. Backend
      // silently merges if the other-provider account is empty (0 eSIMs +
      // 0 payments + 0 tickets), else returns 409 with a friendly error.
      const linkSection = document.querySelector<HTMLElement>('[data-link-accounts-section]');
      if (linkSection) {
        void (async () => {
          const { listProvidersWithStatus, linkGoogle, linkTelegram } = await import('../../account-link');
          const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
          const telegramBotUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME;
          const TG_WIDGET_SRC = 'https://telegram.org/js/telegram-widget.js?22';
          const GIS_SRC = `https://accounts.google.com/gsi/client?hl=${getLocale()}`;

          const setStatus = (provider: 'google' | 'telegram', linked: boolean, identifier: string | null): void => {
            const statusEl = linkSection.querySelector<HTMLElement>(`[data-link-status="${provider}"]`);
            const addBtn = linkSection.querySelector<HTMLButtonElement>(`[data-link-add="${provider}"]`);
            const idEl = linkSection.querySelector<HTMLElement>(`[data-link-identifier="${provider}"]`);
            if (statusEl) {
              statusEl.textContent = linked
                ? t('dash.settings.statusLinked')
                : t('dash.settings.statusNotLinked');
            }
            // Show identifier (email for Google, @username for Telegram) below
            // the "Linked" status so the user knows WHICH account is wired up.
            // Operator pain 2026-05-09 +18: «надо email или тг отразить» —
            // bare "Linked" gives no info if user has multiple Google accounts
            // or multiple TG handles in their lifetime.
            if (idEl) {
              if (linked && identifier) {
                idEl.textContent = identifier;
                idEl.style.display = '';
              } else {
                idEl.textContent = '';
                idEl.style.display = 'none';
              }
            }
            // Use inline style.display rather than the `hidden` attribute —
            // `.btn { display: inline-flex }` (base.css line 107) wins over
            // the UA's `[hidden] { display: none }` due to specificity, so
            // the attribute alone never actually hides the button. Operator
            // caught this 2026-05-09 +16: «кнопки add не работают нигде».
            if (addBtn) addBtn.style.display = linked ? 'none' : '';
          };

          // Map raw `sessions.origin` strings to user-readable labels for
          // the "Currently signed in via X" hint. Unknown origins fall
          // through to a generic label so the hint is always meaningful.
          const labelForOrigin = (origin: string | null): string | null => {
            if (!origin) return null;
            if (origin === 'google') return t('dash.settings.providerGoogle');
            if (origin === 'telegram' || origin === 'telegram-webapp') return t('dash.settings.providerTelegram');
            if (origin === 'passkey') return t('dash.settings.linkOriginPasskey');
            if (origin === 'admin-impersonate') return t('dash.settings.linkOriginImpersonate');
            if (origin === 'dev') return t('dash.settings.linkOriginDev');
            return origin;
          };

          const refresh = async (): Promise<void> => {
            const r = await listProvidersWithStatus();
            if (!r.ok) {
              // Audit +21/M-9: distinguish 401 (session expired — caller
              // needs to re-auth) from 5xx/network (transient backend issue).
              const gStatusEl = linkSection.querySelector<HTMLElement>('[data-link-status="google"]');
              const tStatusEl = linkSection.querySelector<HTMLElement>('[data-link-status="telegram"]');
              if (r.reason === 'unauthenticated') {
                // Session died (cookie expired, server rotated key, etc).
                // Re-route to home with auth modal — same UX as Auth.init's
                // 401 handling. Surface a small toast so it doesn't feel
                // like an opaque redirect.
                showToast(t('toast.sessionExpired'), 'info');
                window.history.pushState({}, '', withLocale('/'));
                window.dispatchEvent(new PopStateEvent('popstate'));
                return;
              }
              // 5xx / network — leave the "Verifying…" placeholder swapped
              // for an explicit error so operator can spot a backend issue.
              if (gStatusEl) gStatusEl.textContent = t('dash.settings.linkFailed');
              if (tStatusEl) tStatusEl.textContent = t('dash.settings.linkFailed');
              return;
            }
            const info = r.info;
            // Identifier strings: email (Google) and @username (TG) come
            // from the same payload. For TG, prefix '@' so it reads as a
            // handle. Empty/null → don't show the line.
            const tgHandle = info.telegramUsername ? `@${info.telegramUsername}` : null;
            setStatus('google', info.providers.google, info.email);
            setStatus('telegram', info.providers.telegram, tgHandle);
            // Origin hint: visible when origin is known. Helps admins
            // understand impersonate sessions, helps users feel
            // "yes, the system knows how I'm signed in".
            const hintEl = linkSection.querySelector<HTMLElement>('[data-link-origin-hint]');
            if (hintEl) {
              const lbl = labelForOrigin(info.currentOrigin);
              if (lbl) {
                hintEl.textContent = t('dash.settings.linkCurrentlyVia', { provider: lbl });
                hintEl.style.display = '';
              } else {
                hintEl.style.display = 'none';
              }
            }
          };

          await refresh();

          // ── Google link flow ──
          // Lazy-load GIS, render the official "Sign in with Google" button
          // inline (same widget the auth modal uses), wire the callback to
          // POST the idToken to /api/auth/link/google.
          let gisLoaded: Promise<void> | null = null;
          const ensureGis = (): Promise<void> => {
            if (gisLoaded) return gisLoaded;
            gisLoaded = new Promise<void>((resolve, reject) => {
              const w = window as unknown as { google?: { accounts?: { id?: unknown } } };
              if (w.google?.accounts?.id) return resolve();
              const s = document.createElement('script');
              s.src = GIS_SRC; s.async = true;
              s.onload = () => resolve();
              s.onerror = () => reject(new Error('gis_load_failed'));
              document.head.appendChild(s);
            });
            return gisLoaded;
          };

          const onGoogleClick = async (): Promise<void> => {
            if (!googleClientId) {
              showToast(t('dash.settings.linkFailed'), 'error');
              return;
            }
            const mount = linkSection.querySelector<HTMLElement>('[data-link-mount-google]');
            if (!mount) return;
            mount.style.display = '';
            mount.innerHTML = `<p class="dash-sub" data-i18n="auth.verifying">${escapeHtml(t('auth.verifying'))}</p>`;
            try {
              await ensureGis();
            } catch {
              mount.innerHTML = `<p class="dash-sub dash-error" data-i18n="dash.settings.linkFailed">${escapeHtml(t('dash.settings.linkFailed'))}</p>`;
              return;
            }
            const gis = (window as unknown as {
              google: { accounts: { id: {
                initialize: (opts: { client_id: string; callback: (resp: { credential: string }) => void; ux_mode?: string }) => void;
                renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
              } } };
            }).google.accounts.id;
            mount.innerHTML = '';
            gis.initialize({
              client_id: googleClientId,
              callback: (resp): void => {
                mount.innerHTML = `<p class="dash-sub" data-i18n="auth.verifying">${escapeHtml(t('auth.verifying'))}</p>`;
                void linkGoogle(resp.credential).then(async (r) => {
                  await handleLinkResult('google', r);
                  mount.style.display = 'none';
                  mount.innerHTML = '';
                });
              },
              ux_mode: 'popup',
            });
            gis.renderButton(mount, {
              type: 'standard', theme: 'outline', size: 'large',
              text: 'continue_with', shape: 'rectangular',
              logo_alignment: 'center', locale: getLocale(), width: googleButtonWidth(mount),
            });
          };

          // ── Telegram link flow ──
          // Inject the official login widget; on user-authorize, the
          // global onTelegramLink(user) is invoked → POST /api/auth/link/telegram.
          const onTelegramClick = (): void => {
            if (!telegramBotUsername) {
              showToast(t('dash.settings.linkFailed'), 'error');
              return;
            }
            const mount = linkSection.querySelector<HTMLElement>('[data-link-mount-telegram]');
            if (!mount) return;
            mount.style.display = '';
            mount.innerHTML = `<p class="dash-sub" data-i18n="auth.verifying">${escapeHtml(t('auth.verifying'))}</p>`;
            type TgPayload = import('../../account-link').TelegramAuthPayload;
            (window as unknown as { onTelegramLink?: (u: TgPayload) => void }).onTelegramLink = (payload) => {
              mount.innerHTML = `<p class="dash-sub" data-i18n="auth.verifying">${escapeHtml(t('auth.verifying'))}</p>`;
              void linkTelegram(payload).then(async (r) => {
                await handleLinkResult('telegram', r);
                mount.style.display = 'none';
                mount.innerHTML = '';
              });
            };
            mount.innerHTML = '';
            const s = document.createElement('script');
            s.src = TG_WIDGET_SRC; s.async = true;
            s.setAttribute('data-telegram-login', telegramBotUsername);
            s.setAttribute('data-size', 'large');
            s.setAttribute('data-onauth', 'onTelegramLink(user)');
            s.setAttribute('data-request-access', 'write');
            s.setAttribute('data-lang', getLocale());
            s.onerror = (): void => {
              mount.innerHTML = `<p class="dash-sub dash-error" data-i18n="dash.settings.linkFailed">${escapeHtml(t('dash.settings.linkFailed'))}</p>`;
            };
            mount.appendChild(s);
          };

          const handleLinkResult = async (provider: 'google' | 'telegram', r: import('../../account-link').LinkResult): Promise<void> => {
            if (r.ok) {
              // Narrow via the unique discriminant property on each ok-variant.
              // `'alreadyLinked' in r` etc. is the standard TS-friendly pattern
              // for tagged unions whose tags are properties (not a single
              // `kind`/`type` field).
              if ('alreadyLinked' in r) {
                showToast(t('dash.settings.linkAlreadyLinked'), 'info');
              } else if ('merged' in r) {
                showToast(t('dash.settings.linkSuccessMerged'), 'success');
              } else {
                showToast(t('dash.settings.linkSuccessAttached'), 'success');
              }
              await refresh();
              return;
            }
            if (r.reason === 'has_data') {
              showToast(
                t('dash.settings.linkConflictHasData', {
                  provider: provider === 'google' ? 'Google' : 'Telegram',
                }),
                'error',
              );
            } else if (r.reason === 'source_suspended') {
              // Surface a distinct message so operator/user know this is a
              // policy block (suspended account preserved for forensics),
              // not a "you can't merge" generic conflict.
              showToast(t('dash.settings.linkSourceSuspended'), 'error');
            } else if (r.reason === 'email_collision') {
              showToast(t('dash.settings.linkEmailCollision'), 'error');
            } else if (r.reason === 'link_in_progress') {
              showToast(t('dash.settings.linkInProgress'), 'info');
            } else if (r.reason === 'unauthenticated') {
              // Session died mid-flow — same handling as in refresh().
              showToast(t('toast.sessionExpired'), 'info');
              window.history.pushState({}, '', withLocale('/'));
              window.dispatchEvent(new PopStateEvent('popstate'));
              return;
            } else {
              showToast(t('dash.settings.linkFailed'), 'error');
            }
          };

          const onClick = (e: Event): void => {
            const btn = (e.target as Element | null)?.closest<HTMLElement>('[data-link-add]');
            if (!btn) return;
            e.preventDefault();
            const provider = btn.dataset.linkAdd;
            if (provider === 'google') void onGoogleClick();
            else if (provider === 'telegram') onTelegramClick();
          };
          linkSection.addEventListener('click', onClick);
          cleanups.push(() => {
            linkSection.removeEventListener('click', onClick);
            try { delete (window as unknown as { onTelegramLink?: unknown }).onTelegramLink; } catch { /* */ }
          });
        })();
      }

      return () => cleanups.forEach((fn) => fn());
    },
  };
}

const dashboardIndex: RouteDef = {
  path: '/dashboard',
  routeType: '404',
  authRequired: true,
  metaTitle: 'Dashboard — Roamzy',
  metaDesc: 'Your eSIMs and account.',
  render() {
    queueMicrotask(() => {
      // withLocale: a bare '/dashboard/esims' here sent every /ru|/zh|/es|/pt|/tr
      // visitor to the English dashboard (found 2026-09-29).
      window.history.replaceState({}, '', withLocale('/dashboard/esims'));
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
    return '';
  },
};

export const dashboardRoutes: RouteDef[] = [
  dashboardIndex,
  ...TAB_ORDER.map(buildDashboardRoute),
];
